import { styled } from 'linaria/react';

import { BREAKPOINTS, COLORS, FONTS } from 'styles/constants';
import { ReactComponent as LogoDark } from 'assets/icons/LogoDark.svg';

export const StyledLogoDark = styled(LogoDark)`
  color: #181800;
`;
/* ── Page Wrapper ── */
export const StyledPageWrap = styled.div`
  font-family: ${FONTS.secondary};
  font-weight: 500;
  background: ${COLORS.white.off[100]};
  color: ${COLORS.brown[100]};
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  min-height: 100vh;
`;

/* ── NAV ── */
export const StyledNav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 3rem;
  border-bottom: 1px solid ${COLORS.gray[100]};
  background: ${COLORS.white.off[100]};
  position: sticky;
  top: 0;
  z-index: 100;

  @media (max-width: ${BREAKPOINTS.m}px) {
    padding: 1rem 1.5rem;
  }
`;

export const StyledNavLogo = styled.span`
  font-family: ${FONTS.primary};
  font-size: 1.35rem;
  color: ${COLORS.brown[100]};
  text-decoration: none;
`;

export const StyledNavLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;

  a {
    font-size: 0.875rem;
    font-weight: 500;
    color: ${COLORS.brown.light[100]};
    text-decoration: none;
    transition: color 0.2s;
  }

  a:hover {
    color: ${COLORS.brown[100]};
  }

  a.active {
    color: ${COLORS.brown[100]};
    font-weight: 600;
  }

  @media (max-width: ${BREAKPOINTS.m}px) {
    gap: 1.25rem;
  }
`;

/* ── HERO ── */
export const StyledHero = styled.section`
  padding: 6rem 3rem 5rem;
  max-width: 900px;
  margin: 0 auto;
  text-align: center;

  @media (max-width: ${BREAKPOINTS.m}px) {
    padding: 4rem 1.5rem 3.5rem;
  }
`;

export const StyledHeroBadge = styled.div`
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${COLORS.brown.light[100]};
  background: ${COLORS.brown.dark[8]};
  padding: 0.4rem 1rem;
  border-radius: 100px;
  margin-bottom: 2rem;
`;

export const StyledHeroTitle = styled.h1`
  font-family: ${FONTS.primary};
  font-size: 3.5rem;
  font-weight: 400;
  line-height: 1.15;
  color: ${COLORS.brown.dark[100]};
  margin-bottom: 1.5rem;
  letter-spacing: -0.02em;

  @media (max-width: ${BREAKPOINTS.m}px) {
    font-size: 2.25rem;
  }
`;

export const StyledHeroDesc = styled.p`
  font-size: 1.1rem;
  color: ${COLORS.brown.light[100]};
  max-width: 620px;
  margin: 0 auto 2.5rem;
  line-height: 1.7;
`;

export const StyledBtnPrimary = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: ${COLORS.brown[100]};
  color: ${COLORS.white.off[100]};
  font-family: ${FONTS.secondary};
  font-size: 0.95rem;
  font-weight: 600;
  padding: 0.85rem 2rem;
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.2s, transform 0.15s;
  cursor: pointer;

  &:hover {
    background: ${COLORS.brown.dark[100]};
    transform: translateY(-1px);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const StyledHeroSub = styled.div`
  margin-top: 1.5rem;
  font-size: 0.8rem;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${COLORS.gray[100]};

  strong {
    font-weight: 600;
    color: ${COLORS.brown.light[100]};
  }
`;

/* ── DIVIDER ── */
export const StyledDivider = styled.div`
  width: 60px;
  height: 1px;
  background: ${COLORS.gray[100]};
  margin: 0 auto;
`;

/* ── SECTION LABEL ── */
export const StyledSectionLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${COLORS.gray[100]};
  margin-bottom: 1rem;
`;

/* ── WHY SECTION ── */
export const StyledWhySection = styled.section`
  padding: 5rem 3rem;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: ${BREAKPOINTS.m}px) {
    padding: 3.5rem 1.5rem;
  }
`;

export const StyledWhyTitle = styled.h2`
  font-family: ${FONTS.primary};
  font-size: 2.25rem;
  font-weight: 400;
  color: ${COLORS.brown.dark[100]};
  margin-bottom: 3rem;
  max-width: 600px;
  line-height: 1.25;
`;

export const StyledWhyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;

  @media (max-width: ${BREAKPOINTS.m}px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`;

export const StyledWhyCard = styled.div`
  padding: 2rem;
  background: ${COLORS.white[100]};
  border-radius: 12px;
  border: 1px solid ${COLORS.gray[100]};
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 30px rgba(24, 24, 0, 0.06);
  }

  h3 {
    font-size: 1.05rem;
    font-weight: 600;
    color: ${COLORS.brown.dark[100]};
    margin-bottom: 0.6rem;
  }

  p {
    font-size: 0.9rem;
    color: ${COLORS.brown.light[100]};
    line-height: 1.65;
  }
`;

export const StyledCardIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${COLORS.green.light};
  border-radius: 10px;
  margin-bottom: 1.25rem;
  color: ${COLORS.brown.dark[100]};
`;

/* ── FORMATS SECTION (dark) ── */
export const StyledFormatsSection = styled.section`
  padding: 5rem 3rem;
  background: ${COLORS.brown.dark[100]};

  @media (max-width: ${BREAKPOINTS.m}px) {
    padding: 3.5rem 1.5rem;
  }
`;

export const StyledFormatsInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

export const StyledFormatsLabel = styled(StyledSectionLabel)`
  color: ${COLORS.brown.light[100]};
`;

export const StyledFormatsTitle = styled.h2`
  font-family: ${FONTS.primary};
  font-size: 2.25rem;
  font-weight: 400;
  color: ${COLORS.white.off[100]};
  margin-bottom: 1rem;
  line-height: 1.25;
`;

export const StyledFormatsDesc = styled.p`
  font-size: 1rem;
  color: ${COLORS.gray[100]};
  margin-bottom: 3rem;
  max-width: 560px;
  line-height: 1.7;
`;

export const StyledFormatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;

  @media (max-width: ${BREAKPOINTS.m}px) {
    grid-template-columns: 1fr;
  }
`;

export const StyledFormatCard = styled.div`
  padding: 2rem;
  background: ${COLORS.white['5']};
  border: 1px solid ${COLORS.white['10']};
  border-radius: 12px;
  transition: background 0.2s;

  &:hover {
    background: ${COLORS.white['8']};
  }

  h3 {
    font-size: 1.05rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: ${COLORS.white.off[100]};
  }

  p {
    font-size: 0.875rem;
    color: ${COLORS.gray[100]};
    line-height: 1.65;
  }
`;

/* ── AUDIENCE SECTION ── */
export const StyledAudienceSection = styled.section`
  padding: 5rem 3rem;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: ${BREAKPOINTS.m}px) {
    padding: 3.5rem 1.5rem;
  }
`;

export const StyledAudienceTitle = styled.h2`
  font-family: ${FONTS.primary};
  font-size: 2.25rem;
  font-weight: 400;
  color: ${COLORS.brown.dark[100]};
  margin-bottom: 1rem;
  line-height: 1.25;
`;

export const StyledAudienceDesc = styled.p`
  font-size: 1rem;
  color: ${COLORS.brown.light[100]};
  margin-bottom: 3rem;
  max-width: 600px;
  line-height: 1.7;
`;

export const StyledTopicsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

export const StyledTopicTag = styled.span`
  font-size: 0.8rem;
  font-weight: 500;
  padding: 0.45rem 1rem;
  border-radius: 100px;
  border: 1px solid ${COLORS.gray[100]};
  color: ${COLORS.brown.light[100]};
  background: ${COLORS.white[100]};
  transition: border-color 0.2s, color 0.2s, background 0.2s;
  cursor: default;

  &:hover {
    border-color: ${COLORS.brown[100]};
    color: ${COLORS.brown.dark[100]};
    background: ${COLORS.green.light};
  }
`;

/* ── CTA SECTION ── */
export const StyledCtaSection = styled.section`
  padding: 5rem 3rem;
  text-align: center;
  background: ${COLORS.white[100]};
  border-top: 1px solid ${COLORS.gray[100]};
  border-bottom: 1px solid ${COLORS.gray[100]};

  @media (max-width: ${BREAKPOINTS.m}px) {
    padding: 3.5rem 1.5rem;

    h2 {
      font-size: 1.75rem;
    }
  }
`;

export const StyledCtaInner = styled.div`
  max-width: 620px;
  margin: 0 auto;
`;

export const StyledCtaMark = styled.div`
  font-family: ${FONTS.primary};
  font-size: 1.5rem;
  color: ${COLORS.brown[100]};
  margin-bottom: 1.5rem;
`;

export const StyledCtaTitle = styled.h2`
  font-family: ${FONTS.primary};
  font-size: 2.25rem;
  font-weight: 400;
  color: ${COLORS.brown.dark[100]};
  margin-bottom: 1rem;
  line-height: 1.25;
`;

export const StyledCtaDesc = styled.p`
  font-size: 1rem;
  color: ${COLORS.brown.light[100]};
  margin-bottom: 2.5rem;
  line-height: 1.7;
`;

export const StyledCtaEmail = styled.span`
  display: block;
  margin-top: 1rem;
  font-size: 0.875rem;
  color: ${COLORS.gray[100]};

  a {
    color: ${COLORS.brown[100]};
    text-decoration: none;
    font-weight: 500;
  }

  a:hover {
    text-decoration: underline;
  }
`;

/* ── FOOTER ── */
export const StyledFooter = styled.footer`
  padding: 3rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: ${BREAKPOINTS.m}px) {
    flex-direction: column;
    gap: 1.5rem;
    text-align: center;
    padding: 2.5rem 1.5rem;
  }
`;

export const StyledFooterLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;

  @media (max-width: ${BREAKPOINTS.m}px) {
    align-items: center;
  }
`;

export const StyledFooterMark = styled.div`
  font-family: ${FONTS.primary};
  font-size: 1.15rem;
  color: ${COLORS.brown[100]};
`;

export const StyledFooterMarkSecondary = styled.div`
  font-family: ${FONTS.primary};
  font-size: 1.15rem;
  color: ${COLORS.brown[100]};
`;

export const StyledFooterCopy = styled.div`
  font-size: 0.8rem;
  color: ${COLORS.gray[100]};
  margin-top: 0.5rem;
`;

export const StyledFooterLinks = styled.div`
  display: flex;
  gap: 1.5rem;

  a {
    font-size: 0.825rem;
    color: ${COLORS.brown.light[100]};
    text-decoration: none;
    font-weight: 500;
  }

  a:hover {
    color: ${COLORS.brown[100]};
  }
`;
